// Login-Hintergrund (design_handoff_login §4): Farbverläufe, Skyline mit Fenstern, Sterne (dunkel), schwebende Widgets.
// Rein dekorativ (aria-hidden, pointer-events none, keine echten Daten). Parallax über die CSS-Variablen --mx/--my,
// die useParallax per requestAnimationFrame setzt – React rendert bei Mausbewegung nicht neu.
// Objektfoto public/login/objektfoto.jpg: Platzhalter von Unsplash (photo-1545324418-cc1a3fa10c00, Unsplash-Lizenz),
// lokal abgelegt statt Hotlink – bei Bedarf durch ein eigenes Objektfoto ersetzen.
import { useEffect, useMemo, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { CalendarBlank, ChartBar, EnvelopeSimple, FolderSimple, Key, Ruler, Users, type Icon } from '@phosphor-icons/react';

/** Setzt --mx/--my (−1…1) auf dem Element; aus bei prefers-reduced-motion und ohne Maus. */
export function useParallax(ziel: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ziel.current;
    if (!el || typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    let rahmen = 0;
    let x = 0;
    let y = 0;
    const bewegt = (e: PointerEvent) => {
      x = (e.clientX / (window.innerWidth || 1)) * 2 - 1;
      y = (e.clientY / (window.innerHeight || 1)) * 2 - 1;
      if (rahmen) return;
      rahmen = requestAnimationFrame(() => {
        rahmen = 0;
        el.style.setProperty('--mx', x.toFixed(3));
        el.style.setProperty('--my', y.toFixed(3));
      });
    };
    window.addEventListener('pointermove', bewegt, { passive: true });
    return () => { window.removeEventListener('pointermove', bewegt); if (rahmen) cancelAnimationFrame(rahmen); };
  }, [ziel]);
}

/** Parallax-Versatz in px je Achse (Faktor × --mx/--my). */
export const px = (fx: number, fy: number): string => `translate(calc(var(--mx, 0) * ${fx}px), calc(var(--my, 0) * ${fy}px))`;

/** Deterministischer Zufall, damit Sterne und Fenster bei jedem Rendern gleich liegen. */
function zufall(start: number) {
  let s = start;
  return () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };
}

const verlauf = (token: string) => `linear-gradient(160deg, color-mix(in srgb, var(${token}) 60%, #fff), var(${token}))`;

// [links %, Höhe %, Breite, Antenne]
const HAEUSER: readonly [number, number, number, boolean][] = [
  [0, 70, 26, true], [7, 50, 40, false], [12, 90, 30, false], [20, 60, 46, true], [27, 80, 34, false], [35, 56, 52, false], [43, 74, 38, true],
  [50, 96, 28, false], [57, 62, 48, false], [65, 84, 36, true], [72, 54, 44, false], [79, 90, 32, false], [86, 66, 50, true], [93, 76, 30, false],
];

interface Haus { links: string; breite: number; hoehe: string; flaeche: string; fenster: string; antenne: boolean; ebene: 1 | 2; lichter: Licht[] }
interface Licht { x: number; y: number; an: boolean; anim?: string }

function erzeugeSkyline(mobil: boolean): Haus[] {
  const rnd = zufall(7);
  const hs = mobil ? 0.6 : 1;
  const lichter = (b: number, n: number): Licht[] => {
    const spalten = Math.max(1, Math.floor((b - 16) / 10));
    return Array.from({ length: n }, () => {
      const c = Math.floor(rnd() * spalten), r = Math.floor(rnd() * 7), t = rnd();
      const anim = t < 0.12 ? `loginFlicker ${(3 + rnd() * 4).toFixed(1)}s ease-in-out infinite`
        : t >= 0.75 ? `loginLightOn ${(14 + rnd() * 16).toFixed(1)}s ease-in-out ${(-rnd() * 20).toFixed(1)}s infinite` : undefined;
      return { x: 8 + c * 10, y: 12 + r * 14, an: t < 0.75, anim };
    });
  };
  const vorne = HAEUSER.map(([l, h, b, a]): Haus => {
    const breite = mobil ? b * 0.8 : b * 2.4;
    return { links: `${l}%`, breite, hoehe: `${h * hs}%`, flaeche: 'var(--sky1)', fenster: 'var(--win1)', antenne: a, ebene: 1, lichter: lichter(breite, mobil ? 2 : 4) };
  });
  const hinten = HAEUSER.map(([l, h, b]): Haus => {
    const breite = mobil ? b * 0.7 : b * 2;
    return { links: `${l + 3.5}%`, breite, hoehe: `${h * 0.55 * hs}%`, flaeche: 'var(--sky2)', fenster: 'var(--win2)', antenne: false, ebene: 2, lichter: lichter(breite, mobil ? 2 : 5) };
  });
  return [...vorne, ...hinten];
}

function erzeugeSterne(mobil: boolean) {
  const rnd = zufall(31);
  return Array.from({ length: mobil ? 40 : 90 }, () => {
    const gross = rnd() < 0.12;
    return {
      x: `${(rnd() * 100).toFixed(2)}%`, y: `${(Math.pow(rnd(), 1.4) * 100).toFixed(2)}%`,
      r: gross ? 2 : rnd() < 0.5 ? 1 : 1.5, schein: gross,
      anim: `loginTwinkle ${(2.5 + rnd() * 5).toFixed(1)}s ease-in-out ${(-rnd() * 6).toFixed(1)}s infinite`,
    };
  });
}

/* ---------- Schwebende Widgets ---------- */

type WidgetArt = 'haus' | 'uebergabe' | 'grundriss' | 'weg' | 'mail' | 'mieter' | 'dokumente' | 'kalender';
// [Art, x px ab Mitte, y px ab Mitte (oder ab oben bei mobil), Breite, Höhe, Tiefe, Drehung]
type Platz = [WidgetArt, number, number, number, number, number, number];

const GROSS: Platz[] = [['haus', -490, -260, 230, 190, 0.5, -6], ['uebergabe', -450, 110, 240, 160, 0.9, 4], ['grundriss', 260, -215, 220, 185, 0.7, 5], ['weg', 300, 95, 250, 175, 0.45, -4]];
const KLEIN: Platz[] = [['mail', -300, -60, 76, 76, 1.2, -8], ['mieter', 260, -50, 70, 70, 1.1, 8], ['dokumente', -250, 260, 64, 64, 0.8, 6], ['kalender', 230, 290, 68, 68, 1, -6]];
// Mobil oben am Rand – so gesetzt, dass weder Hell/Dunkel-Schalter (oben rechts) noch Schriftzug verdeckt werden.
const KLEIN_MOBIL: Platz[] = [['mail', -160, 30, 50, 50, 1, -10], ['mieter', 112, 150, 44, 44, 1, 10], ['dokumente', -150, 128, 40, 40, 0.8, 8], ['kalender', 130, 84, 46, 46, 0.9, -8]];

const APP_ICON: Record<'mail' | 'mieter' | 'dokumente', { icon: Icon; token: string }> = {
  mail: { icon: EnvelopeSimple, token: '--info' },
  mieter: { icon: Users, token: '--pur' },
  dokumente: { icon: FolderSimple, token: '--warn' },
};

function KopfZeile({ icon: I, titel, flaeche = 'var(--acc-soft)', farbe = 'var(--acc)' }: { icon: Icon; titel: string; flaeche?: string; farbe?: string }) {
  return (
    <div className="flex items-center" style={{ gap: 10 }}>
      <span className="flex shrink-0 items-center justify-center" style={{ width: 40, height: 40, borderRadius: 12, background: flaeche, color: farbe, boxShadow: 'inset 0 0 0 1px var(--line)' }}>
        <I size={21} weight="duotone" />
      </span>
      <span style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em', color: 'var(--ink)' }}>{titel}</span>
    </div>
  );
}

function WidgetInhalt({ art, groesse }: { art: WidgetArt; groesse: number }): ReactNode {
  switch (art) {
    case 'haus':
      return (
        <div className="absolute inset-0 overflow-hidden" style={{ borderRadius: 28 }}>
          <img src="/login/objektfoto.jpg" alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
          <div className="absolute inset-x-0 bottom-0" style={{ height: '70%', background: 'linear-gradient(to top, rgba(10,18,30,0.75), transparent)' }} />
          <div className="absolute flex flex-col" style={{ left: 16, right: 16, bottom: 14, color: '#fff' }}>
            <span style={{ fontSize: 15, fontWeight: 800 }}>Musterstraße 12</span>
            <span style={{ fontSize: 12, opacity: 0.8 }}>12 Einheiten · Vollvermietet</span>
          </div>
        </div>
      );
    case 'uebergabe':
      return (
        <>
          <KopfZeile icon={Key} titel="Wohnungsübergabe" flaeche={verlauf('--warn')} farbe="#fff" />
          <div className="flex flex-col" style={{ gap: 10 }}>
            <div className="flex items-baseline justify-between" style={{ gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink2)' }}>Whg. 3.OG links</span>
              <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--acc)' }}>Fr · 10:00</span>
            </div>
            <div className="flex" style={{ gap: 4 }}>
              {[1, 1, 1, 0, 0].map((an, i) => <span key={i} style={{ flex: 1, height: 5, borderRadius: 3, background: an ? 'var(--ok)' : 'var(--fill3)' }} />)}
            </div>
          </div>
        </>
      );
    case 'grundriss':
      return (
        <>
          <KopfZeile icon={Ruler} titel="Grundriss" />
          <svg viewBox="0 0 170 100" style={{ width: '100%', height: 'auto', color: 'var(--ink2)' }} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <rect x="4" y="4" width="162" height="92" rx="3" />
            <path d="M70 4 V40 M70 58 V96 M70 56 H120 M138 56 H166 M100 4 V30" />
            <path d="M70 40 A18 18 0 0 1 88 58" strokeWidth={1.2} strokeDasharray="3 3" />
            <rect x="12" y="12" width="40" height="24" rx="3" strokeWidth={1.2} fill="var(--acc-soft)" />
            <circle cx="140" cy="76" r="8" strokeWidth={1.2} />
          </svg>
        </>
      );
    case 'weg':
      return (
        <>
          <KopfZeile icon={ChartBar} titel="WEG-Buchhaltung" flaeche={verlauf('--acc')} farbe="#fff" />
          <div className="flex items-end" style={{ gap: 5, height: 44 }}>
            {[52, 68, 60, 84, 72, 96].map((h, i) => (
              <span key={i} style={{ flex: 1, height: `${h}%`, borderRadius: 4, background: i === 5 ? 'var(--acc-fill)' : 'var(--acc-soft)' }} />
            ))}
          </div>
        </>
      );
    case 'kalender': {
      const heute = new Date();
      return (
        <span className="flex h-full w-full flex-col items-center justify-center" style={{ borderRadius: groesse * 0.26, background: 'var(--sheet)', boxShadow: 'inset 0 0 0 0.5px var(--line)' }}>
          <span style={{ fontSize: groesse * 0.15, fontWeight: 800, letterSpacing: '0.04em', color: 'var(--bad)', lineHeight: 1 }}>
            {['SO', 'MO', 'DI', 'MI', 'DO', 'FR', 'SA'][heute.getDay()]}
          </span>
          <span style={{ fontSize: groesse * 0.46, fontWeight: 500, letterSpacing: '-0.04em', color: 'var(--ink)', lineHeight: 1 }}>{heute.getDate()}</span>
        </span>
      );
    }
    default: {
      const { icon: I, token } = APP_ICON[art];
      return (
        <span className="flex h-full w-full items-center justify-center"
          style={{ borderRadius: groesse * 0.26, background: verlauf(token), color: '#fff', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55)' }}>
          <I size={groesse * 0.5} weight="fill" />
        </span>
      );
    }
  }
}

function Widget({ platz, index, mobilOben }: { platz: Platz; index: number; mobilOben: boolean }) {
  const [art, x, y, b, h, tiefe, drehung] = platz;
  const gross = ['haus', 'uebergabe', 'grundriss', 'weg'].includes(art);
  const hinten = tiefe < 0.6;
  const aussen: CSSProperties = {
    position: 'absolute', left: `calc(50% + ${x}px)`, top: mobilOben ? `${y}px` : `calc(50% + ${y}px)`,
    animation: `${index % 2 ? 'loginFloatB' : 'loginFloatA'} ${7 + (index % 3)}s ease-in-out ${-index * 1.3}s infinite`,
  };
  const innen: CSSProperties = {
    position: 'relative', width: b, height: h, borderRadius: gross ? 28 : b * 0.26,
    transform: `${px(tiefe * 18, tiefe * 14)} rotate(${drehung}deg)`,
    filter: hinten ? 'blur(2px)' : undefined, opacity: hinten ? 0.75 : 1,
    ...(gross ? {
      padding: art === 'haus' ? 0 : 18, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 10,
      background: 'var(--glass)', backdropFilter: 'blur(24px) saturate(170%)', WebkitBackdropFilter: 'blur(24px) saturate(170%)', boxShadow: 'var(--glass-sh)',
    } : { boxShadow: '0 14px 28px -14px rgba(0,0,0,0.45)' }),
  };
  return (
    <div style={aussen}>
      <div className="login-px" style={innen}><WidgetInhalt art={art} groesse={b} /></div>
    </div>
  );
}

export function LoginHintergrund({ breite }: { breite: number }) {
  const mobil = breite < 640;
  const skyline = useMemo(() => erzeugeSkyline(mobil), [mobil]);
  const sterne = useMemo(() => erzeugeSterne(mobil), [mobil]);
  const plaetze: Platz[] = breite >= 1100
    ? [...GROSS, ...KLEIN]
    : breite >= 640 ? KLEIN.map(([a, x, y, b, h, t, d]): Platz => [a, x * 1.15, y * 1.3, b, h, t, d]) : KLEIN_MOBIL;

  return (
    <div aria-hidden className="login-buehne pointer-events-none fixed inset-0 overflow-hidden" style={{ zIndex: 0 }}>
      <div className="fixed" style={{ inset: '-4%', background: 'var(--wall)', animation: 'loginDrift 18s ease-in-out infinite' }} />

      <div className="fixed" style={{ inset: '0 0 30% 0', opacity: 'var(--star)', transition: 'opacity .6s' }}>
        {sterne.map((s, i) => (
          <span key={i} className="absolute" style={{ left: s.x, top: s.y, width: s.r, height: s.r, borderRadius: '50%', background: '#fff',
            boxShadow: s.schein ? '0 0 4px rgba(255,255,255,0.8)' : undefined, animation: s.anim }} />
        ))}
        <span className="absolute" style={{ right: '18%', top: '12%', width: 90, height: 1.5, borderRadius: 1, opacity: 0,
          background: 'linear-gradient(to right, #fff, transparent)', animation: 'loginShoot 14s ease-in 4s infinite' }} />
      </div>

      <div className="fixed inset-x-0 bottom-0" style={{ height: '50vh', background: 'var(--haze)' }} />

      <div className="fixed bottom-0" style={{ left: '-3%', right: '-3%', height: '40vh' }}>
        {skyline.map((haus, i) => (
          <div key={i} className="login-px absolute bottom-0"
            style={{ left: haus.links, width: haus.breite, height: haus.hoehe, borderRadius: '6px 6px 0 0', background: haus.flaeche, transform: px(haus.ebene === 1 ? -6 : -14, 0) }}>
            <div className="absolute" style={{ inset: '12px 8px 0',
              backgroundImage: `linear-gradient(to bottom, transparent 62%, ${haus.flaeche} 62%), linear-gradient(to right, ${haus.fenster} 50%, transparent 50%)`,
              backgroundSize: '100% 14px, 10px 100%' }} />
            {haus.lichter.map((l, k) => (
              <span key={k} className="absolute" style={{ left: l.x, top: l.y, width: 5, height: 8, borderRadius: 1, background: 'var(--lit)', boxShadow: 'var(--lit-glow)',
                opacity: l.an ? 1 : 0, animation: l.anim }} />
            ))}
            {haus.antenne && <div className="absolute" style={{ left: '50%', top: -18, width: 2, height: 18, background: haus.flaeche }} />}
          </div>
        ))}
        <div className="absolute inset-x-0 bottom-0" style={{ height: '36%', background: 'linear-gradient(to top, var(--bg), transparent)' }} />
      </div>

      <div className="fixed inset-0 overflow-hidden">
        {plaetze.map((p, i) => <Widget key={`${p[0]}-${breite >= 1100 ? 'g' : breite >= 640 ? 'm' : 'k'}`} platz={p} index={i} mobilOben={breite < 640} />)}
      </div>
    </div>
  );
}
